import { Button, Spin, Form, message, Popover, Modal, Input, Tooltip, Alert } from 'antd';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { cloneDeep, debounce, isEmpty, isEqual } from 'lodash';
import MappingRulesBuilder from '@src/emp-3.0/components/MappedEventBuilder/MappingRulesBuilder';
import { ActionsWrapper, Header, Wrapper } from '@src/emp-3.0/components/MappedEventBuilder/styled';
import { IMappedEventBuilder } from '@src/emp-3.0/components/MappedEventBuilder/typed';
import { IMappedEvent } from '@src/pages/metric-templates/types';
import Footer from './Footer';
import { useCheckDuplicateMappedEvent } from '@src/emp-3.0/api/queryHooks';
import { StyledFormError } from '@src/pages/workflow/components/BuilderWrapper/styled';
import CustomFormItem from '../Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import { validateMappedEventName } from '@src/emp-3.0/domain/mapped-event';
import {
  isEmptyMappedEventRules,
  trimMappedEventRules,
} from '@src/components/mapping-panel/utils/trimUtils';
import { mappedEventDeployedStatus } from '@src/common/constant/enum';
import { DuplicatePopupContent } from './DuplicatePopupContent.tsx';
import { DEFAULT_REQUIRED_ERROR_MESSAGE } from '@src/emp-3.0/constants/common';

// TODO change onMap to onMap(rules)
const MappedEventBuilder: React.FC<IMappedEventBuilder> = ({
  loading,
  data,
  onCancel,
  onMap,
  onChange,
  onSwitch,
  footer,
  validation,
  setValidation,
  cancelText,
  isBadEvent,
  onValidate,
}) => {
  const [mappedEvent, setMappedEvent] = useState<IMappedEvent | null>(null);
  const [mappedEventClone, setMappedEventClone] = useState<any>(null);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  const [duplicateMappedEventName, setDuplicateMappedEventName] = useState<string>('');
  const [duplicateMappedEventId, setDuplicateMappedEventId] = useState<number | null>(null);
  const [footerError, setFooterError] = useState<boolean>(false);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState<boolean>(false);
  const [eventName, setEventName] = useState<string>('');
  const [footerForm] = Form.useForm();
  const [eventNameForm] = Form.useForm();

  const { mutate: checkDuplicateMappedEvent, isLoading: isDuplicateCheckLoading } =
    useCheckDuplicateMappedEvent();

  const isEmptyRules = useMemo(
    () => isEmptyMappedEventRules(mappedEvent?.rules),
    [mappedEvent?.rules],
  );

  useEffect(() => {
    const cloned = cloneDeep(data);
    setMappedEvent(cloned);
    footerForm.setFieldsValue({ ...cloned });
  }, [data]);

  useEffect(() => {
    if (!isEqual(mappedEvent, data) && mappedEvent) {
      onChange?.({ ...mappedEvent });
    }
    setShowWarning(
      mappedEvent?.status === mappedEventDeployedStatus.deployed ||
        mappedEvent?.related_flows?.length > 0 ||
        mappedEvent?.related_metrics?.length > 0,
    );
  }, [mappedEvent]);

  const handleRulesChange = useCallback(
    (value) => {
      setMappedEvent({
        ...mappedEvent,
        rules: value,
      });
      setDuplicateMappedEventName('');
    },
    [mappedEvent],
  );

  const onCheckDuplicateBeforeCreate = (eventName?: string) => {
    if (!mappedEvent) return;
    let duplicateMappedEventName = '';
    let duplicateMappedEventId = null as number | null;
    const mappedEventClone = {
      ...mappedEvent,
      event_name: eventName || mappedEvent.event_name,
      event_id: eventName ? undefined : mappedEvent.event_id,
      id: eventName ? undefined : mappedEvent.id,
      related_flows: eventName ? [] : mappedEvent.related_flows,
      related_metrics: eventName ? [] : mappedEvent.related_metrics,
      rules: trimMappedEventRules(mappedEvent.rules),
    };
    setMappedEventClone(mappedEventClone);
    if (isBadEvent) {
      // Bad event should not check duplicate
      onMap?.(mappedEventClone);
      return;
    }

    checkDuplicateMappedEvent(mappedEventClone, {
      onSuccess: (res) => {
        const dupEvents = res.data.filter((dup) => dup.id !== data?.event_id);
        duplicateMappedEventName = dupEvents[0]?.event_name || '';
        duplicateMappedEventId = dupEvents[0]?.id || null;
        if (!duplicateMappedEventName) {
          onMap?.(mappedEventClone);
          return;
        }
        setShowDuplicatePopup(true);
        setDuplicateMappedEventName(duplicateMappedEventName);
        setDuplicateMappedEventId(duplicateMappedEventId);
      },
      onError: () => {
        message.error('Failed To Check Duplicate Mapped Event');
        onMap?.(mappedEventClone);
      },
    });
  };

  const duplicatePopupContent = (
    <DuplicatePopupContent
      duplicateMappedEventName={duplicateMappedEventName}
      text={'Either modify this event rule or use the existing event.'}
      footer={
        <>
          <Button
            onClick={() => {
              setDuplicateMappedEventName('');
              setShowDuplicatePopup(false);
            }}
          >
            Edit This Rule
          </Button>
          <Button
            onClick={() => {
              setShowDuplicatePopup(false);
              onSwitch?.(duplicateMappedEventId);
            }}
          >
            Use Existing Event
          </Button>
        </>
      }
    ></DuplicatePopupContent>
  );

  const validateBeforeCreate = () => {
    footerForm
      .validateFields()
      .then((values) => {
        if (!isEmpty(values)) {
          if (!values.event_name) {
            setFooterError(true);
            return;
          }
        }
        onCheckDuplicateBeforeCreate();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const onSaveClick = (e) => {
    e.stopPropagation();
    if (!isBadEvent) {
      const status = onValidate?.();
      if (!status) {
        return;
      }
    }

    validateBeforeCreate();
  };

  const { relatedMetrics, relatedFlows } = useMemo(() => {
    return {
      relatedMetrics: mappedEvent?.related_metrics || [],
      relatedFlows: mappedEvent?.related_flows || [],
    };
  }, [mappedEvent]);
  const renderRelatedList = (list: string[]) => {
    return (
      <ul style={{ paddingLeft: '20px', margin: 0 }}>
        {list.map((item, index) => {
          return <li key={item + index}>{item}</li>;
        })}
      </ul>
    );
  };
  const debouncedOnValuesChange = useCallback(
    debounce((values) => {
      if ('event_name' in values) {
        setFooterError(false);
      }
      setMappedEvent((preState) => ({
        ...preState,
        ...values,
      }));
    }, 300),
    [],
  );
  return (
    <Wrapper>
      <Spin spinning={loading}>
        {showWarning ? (
          <Alert
            message={
              !relatedMetrics.length && !relatedFlows.length ? (
                <span>
                  This is a deployed event. Changes will impact your analysis going forward.
                </span>
              ) : (
                <span>
                  Changes to this event will impact&nbsp;
                  {relatedMetrics.length ? (
                    <Tooltip title={renderRelatedList(relatedMetrics)}>
                      <u>{`${relatedMetrics.length} metric(s)`}</u>
                    </Tooltip>
                  ) : null}
                  {relatedMetrics.length && relatedFlows.length ? ' and ' : null}
                  {relatedFlows.length ? (
                    <Tooltip title={renderRelatedList(relatedFlows)}>
                      <u>{`${relatedFlows?.length} flow(s)`}</u>
                    </Tooltip>
                  ) : null}
                </span>
              )
            }
            banner
            closable
          />
        ) : null}
        <Header>
          {footer !== undefined ? (
            footer
          ) : (
            <Footer
              form={footerForm}
              eventNameError={footerError || validation?.fields?.includes('mapped_event_name')}
              onValidate={(value) => {
                setValidation?.(value);
              }}
              onValuesChange={debouncedOnValuesChange}
            />
          )}
        </Header>
        {(validation?.failed || footerError) && !validation?.fields?.includes('event_name') && (
          <StyledFormError>{validation?.message || DEFAULT_REQUIRED_ERROR_MESSAGE}</StyledFormError>
        )}
        <MappingRulesBuilder
          validation={validation}
          rules={mappedEvent?.rules}
          onChange={handleRulesChange}
        />
      </Spin>
      <ActionsWrapper>
        <Button onClick={onCancel}>{cancelText ?? 'Cancel'}</Button>
        <Popover
          content={duplicatePopupContent}
          open={showDuplicatePopup}
          trigger={'click'}
          onOpenChange={(v) => {
            !v && setShowDuplicatePopup(v);
          }}
        >
          <Button
            style={!isEmptyRules ? { color: '#0046ff', border: '1px solid #0046ff' } : undefined}
            loading={loading || isDuplicateCheckLoading}
            disabled={duplicateMappedEventName !== '' || isEmptyRules}
            onClick={onSaveClick}
          >
            Save
          </Button>
        </Popover>
      </ActionsWrapper>
    </Wrapper>
  );
};

export default React.memo(MappedEventBuilder);
